import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadPopularCategory from "@tIbubb/Handlers/Categories/GetCategories";
import { FeaturedCategory } from "@tIbubb/Serivices/PopularCategories";
import CustomApplicationState from "@tIbubb/Store/Data/CustomApplicationState/CustomApplicationState";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    viewAllCategories = "viewAllCategories",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        [fields.viewAllCategories]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState) => ({
    categoriesDataView: state.IBUData.popularCategoryStore,
});

const mapDispatchToProps = {
    loadPopularCategory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryHeading?: InjectableCss;
    titleText?: InjectableCss;
    allCategoryLink?: InjectableCss;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
}

export const categoryListStyles: CategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 30,
        css: css`
            max-width: 1300px;
            margin: 0 auto;
            > div[class^="GridOffset"] {
                // width: 100%;
                // margin: 0px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 1300px;
                        margin: 0 auto;
                        margin-bottom: 36px;
                    `,
                ])}
        `,
    },
    categoryHeading: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            max-width: 1300px;
            margin: 0 auto;
            padding: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 1300px;
                        margin: 0 auto;
                    `,
                ])}
        `,
    },
    titleText: {
        css: css`
            letter-spacing: 0px;
            font-size: 22px;
            font-weight: normal;
            line-height: 1;
            margin-bottom: 36px;
            padding-top: 36px;
        `,
    },
    categoryNameLink: {
        css: css`
            height: 100%;
        `,
    },
    allCategoryLink: {
        css: css`
            letter-spacing: 0px;
            font-family: inherit;
            min-height: 40px;
            font-size: 16px;
            font-weight: normal;
            line-height: 1;
            padding: 0 21px;
            border-radius: 20px;
            border: 2px solid;
            border-color: ${getColor("primary")};
            text-align: center;
            span {
                color: ${getColor("primary")} !important;
            }
            transition: all 200ms ease-in-out;
            &:hover {
                background-color: ${getColor("primary")};
                span {
                    color: #fff !important;
                }
            }
        `,
    },
    categoryItem: {
        // width is dynamic here
        css: css`
            max-width: 20%;
            flex-basis: 20%;
            div[class^="GridWrapper"] {
                height: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 50%;
                        flex-basis: 50%;
                    `,
                    css`
                        max-width: 50%;
                        flex-basis: 50%;
                    `,
                    css`
                        max-width: 33.33%;
                        flex-basis: 33.33%;
                    `,
                    css`
                        max-width: 25%;
                        flex-basis: 25%;
                    `,
                    css``,
                ])}
        `,
    },
    innerContainer: {
        gap: 10,
        css: css`
            > div[class^="GridOffset"] {
                width: 100%;
                margin: auto;
                height: 100%;
            }
            transition: box-shadow 0.2s ease-in-out 0s;
            &:hover {
                box-shadow: rgb(33 33 33 / 50%) 0px 6px 10px 0px;
            }
        `,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            height: 100%;
            padding: 0px;
            border: 1px solid rgb(204, 204, 204);
            box-shadow: rgb(255 255 255) 5px 10px 18px;
            &:hover {
                border: 1px solid ${getColor("primary")};
            }
        `,
    },
    categoryImage: {
        // height: "160px",
        css: css`
            width: 100%;
            display: block;

            img {
                height: 100%;
                width: 100%;
                aspect-ratio: 1/1;
            }
        `,
    },
    categoryImageLink: {
        css: css`
            width: 100%;
            display: block;
            span {
                width: 100%;
            }
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            background-color: ${getColor("primary")};
            justify-content: center;
            height: 100%;
            text-align: center;
        `,
    },
    categoryName: {
        css: css`
            color: #fff;
            font-weight: bold;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            font-weight: bold;
            padding-top: 10px;
        `,
    },
};

const styles = categoryListStyles;

const CategoryList: React.FC<Props> = ({ categoriesDataView, loadPopularCategory, fields }) => {
    React.useEffect(() => {
        if (categoriesDataView.value === undefined && !categoriesDataView.isLoading) {
            loadPopularCategory({ pageSize: 10 });
        }
    }, [categoriesDataView.isLoading]);

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.categoryHeading}>
                <Typography as="h2" {...styles.titleText}>
                    Product Categories
                </Typography>
                <Link href={fields.viewAllCategories} {...styles.allCategoryLink}>
                    View All Product Categories
                </Link>
            </StyledWrapper>
            <GridContainer {...styles.container}>
                {categoriesDataView?.value?.featuredCategories?.map((category: FeaturedCategory) => (
                    <>
                        {category.smallImagePath && category.path && (
                            <GridItem key={category.id.toString()} {...styles.categoryItem}>
                                <GridContainer {...styles.innerContainer}>
                                    {fields.showImages && (
                                        <GridItem {...styles.categoryImageItem}>
                                            <StyledWrapper
                                                css={css`
                                                    display: inline-flex;
                                                    flex-direction: column;
                                                    height: 100%;
                                                    width: 100%;
                                                `}
                                            >
                                                {category.path && (
                                                    <Link href={`${category.path}`} {...styles.categoryImageLink}>
                                                        <LazyImage
                                                            src={category.largeImagePath || category.smallImagePath}
                                                            {...styles.categoryImage}
                                                            altText={category.imageAltText}
                                                        />
                                                    </Link>
                                                )}
                                                <StyledWrapper {...styles.categoryNameLinkItem}>
                                                    <Link href={`${category.path}`} {...styles.categoryNameLink}>
                                                        <Typography {...styles.categoryName}>
                                                            {category.shortDescription}
                                                        </Typography>
                                                    </Link>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </GridItem>
                        )}
                    </>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryList),
    definition: {
        group: "Categories",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.viewAllCategories,
                displayName: "View All Categories",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
